import React from 'react';

function FamilyBudgetPrivacy() {
  return (
    <div className="family-budget-privacy-page">
      <section className="privacy-hero">
        <div className="container">
          <h1 className="privacy-title">Privacy Policy</h1>
          <p className="privacy-subtitle">Family Budget</p>
          <p className="privacy-subtitle">Last updated: August 2025</p>
        </div>
      </section>

      <section className="privacy-content">
        <div className="container">
          <div className="privacy-text">
            <div className="privacy-section">
              <h2>1. Introduction</h2>
              <p>
                Welcome to Family Budget. This Privacy Policy explains how we collect, use, and protect your information 
                when you use our mobile application. By using Family Budget, you agree to the collection and use of 
                information in accordance with this policy.
              </p>
              <p>
                <strong>Important:</strong> Family Budget is designed with your financial privacy as our top priority. 
                All your financial data is stored locally on your device and is never transmitted to our servers or 
                shared with third parties.
              </p>
            </div>

            <div className="privacy-section">
              <h2>2. Information We Collect</h2>
              <h3>2.1 Information You Provide</h3>
              <p>Family Budget collects the following information that you provide:</p>
              <ul>
                <li>Expense entries and amounts</li>
                <li>Budget categories and limits</li>
                <li>Financial goals and targets</li>
                <li>App settings and preferences</li>
                <li>Reminder and notification preferences</li>
              </ul>

              <h3>2.2 Automatically Collected Information</h3>
              <p>The app may collect the following information automatically:</p>
              <ul>
                <li>Device information (operating system, app version)</li>
                <li>Usage statistics (app launch times, feature usage)</li>
                <li>Crash reports and error logs (if enabled)</li>
                <li>App performance metrics</li>
              </ul>
            </div>

            <div className="privacy-section">
              <h2>3. How We Use Your Information</h2>
              <p>We use the collected information for the following purposes:</p>
              <ul>
                <li>To provide and maintain the budgeting functionality</li>
                <li>To calculate and display your spending analytics</li>
                <li>To send you budget alerts and reminders (if enabled)</li>
                <li>To display your progress toward financial goals</li>
                <li>To improve the app's performance and user experience</li>
                <li>To fix bugs and technical issues</li>
              </ul>
            </div>

            <div className="privacy-section">
              <h2>4. Data Storage and Security</h2>
              <p>
                <strong>Local Storage Only:</strong> All your financial data, including expenses, budgets, and goals, 
                are stored locally on your device using secure local storage. This means your sensitive financial 
                information never leaves your device.
              </p>
              <p>
                <strong>No Cloud Storage:</strong> Family Budget does not upload your financial data to any cloud 
                servers, external databases, or third-party services. Your data remains completely private and 
                under your control on your device.
              </p>
              <p>
                <strong>Data Security:</strong> We implement appropriate security measures to protect your information 
                against unauthorized access, alteration, or destruction. The app leverages your device's built-in 
                security features.
              </p>
              <p>
                <strong>Backup Considerations:</strong> If you use your device's backup features (iCloud, Google Drive, etc.), 
                your app data may be included in those backups. Please review your device's backup settings and policies.
              </p>
            </div>

            <div className="privacy-section">
              <h2>5. Data Sharing</h2>
              <p>
                <strong>No Data Sharing:</strong> We do not sell, trade, or otherwise transfer your personal or 
                financial information to third parties. Your data is kept completely private and is not shared 
                with any external services or companies.
              </p>
              <p>
                The only exceptions are:
              </p>
              <ul>
                <li>When required by law or to comply with legal processes</li>
                <li>To protect our rights, property, or safety</li>
                <li>With your explicit consent for specific features (if any are added in the future)</li>
              </ul>
            </div>

            <div className="privacy-section">
              <h2>6. Third-Party Services</h2>
              <p>
                Family Budget may use the following third-party services:
              </p>
              <ul>
                <li><strong>Device Notifications:</strong> For sending budget alerts and reminders (uses your device's notification system)</li>
                <li><strong>App Store Analytics:</strong> Basic usage statistics provided by the app store (iOS App Store/Google Play)</li>
                <li><strong>Local Storage:</strong> For secure data storage on your device</li>
              </ul>
              <p>
                These services have their own privacy policies, and we encourage you to review them. 
                Importantly, none of these services have access to your financial data.
              </p>
            </div>

            <div className="privacy-section">
              <h2>7. Your Rights</h2>
              <p>You have the right to:</p>
              <ul>
                <li>Delete your data by uninstalling the app or utilizing the setting menu and selecting clear all data</li>
                <li>Control notification and reminder settings</li>
                <li>Modify your budget categories and financial goals</li>
                <li>Export your data</li>
              </ul>
            </div>

            <div className="privacy-section">
              <h2>8. Financial Data Protection</h2>
              <p>
                <strong>No Banking Information:</strong> Family Budget does not collect, store, or process any 
                banking information, account numbers, or credit card details. The app only stores the expense 
                amounts and categories that you manually enter.
              </p>
              <p>
                <strong>No Transaction Data:</strong> We do not access or import any transaction data from your 
                bank accounts or credit cards. All expense entries are manually created by you.
              </p>
              <p>
                <strong>Local Processing:</strong> All financial calculations, budget tracking, and analytics 
                are performed locally on your device using the data you provide.
              </p>
            </div>

            <div className="privacy-section">
              <h2>9. Children's Privacy</h2>
              <p>
                Family Budget is not intended for children under the age of 13. We do not knowingly collect 
                personal information from children under 13. Personal and financial information is not transmitted from the device regardless of user age.
              </p>
            </div>

            <div className="privacy-section">
              <h2>10. Changes to This Privacy Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. Minor updates may be made without notice however changes regarding third parties or your personal data beibg transmitted off your device will be posted in the app and released as a new major version.
                You are advised to review this Privacy Policy periodically for any changes.
              </p>
            </div>

            <div className="privacy-section">
              <h2>11. Contact Information</h2>
              <p>
                If you have any questions about this Privacy Policy or our data practices, please contact us:
              </p>
              <div className="contact-info-privacy">
                <p><strong>Developer:</strong> Cary Schweitzer</p>
                <p><strong>Contact:</strong> <a href="/contact">Contact</a></p>
              </div>
            </div>

            <div className="privacy-section">
              <h2>12. Governing Law</h2>
              <p>
                This Privacy Policy is governed by and construed in accordance with the laws of Texas, United States. 
                Any disputes arising from this Privacy Policy will be subject to the exclusive jurisdiction of the 
                courts in Texas.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default FamilyBudgetPrivacy;
